import { useState, useEffect, useRef } from "react";
import {
    AlertColor,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stepper,
    Step,
    StepLabel
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ObjectivePage from "./pages/ObjectivePage";
import MediaPage from "./pages/MediaPage";
import AudiencePage from "./pages/AudiencePage";
import adService from "../../../services/ad.service";
import timeout from "../../../hooks/timeout";
import LoadingOverlay from "../../Shared/LoadingOverlay";
import { handleAdCreate, handleAdUpdate } from "./hooks/handleAdCreate";
import { FormSnackbar } from "../../Shared/Form";
import { CreateCampaignViewModel } from "./ViewModel";
import { Campaign } from "../../../types/campaign";

type CreateCampaignType = {
    fetchData: any;
    open: boolean;
    setOpen: (v: boolean) => void;
    ad?: Campaign;
}

const steps = ['Objective', 'Ad Details', 'Audience Targeting'];

const CreateCampaign = ({ fetchData, open, setOpen, ad }: CreateCampaignType) => {
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const [snackbar, setSnackbar] = useState("");
    const [severity, setSeverity] = useState<AlertColor>("success");

    const viewModel = CreateCampaignViewModel();
    const viewModelRef = useRef(viewModel);
    const {
        templateId,
        ageMin,
        ageMax,
        hittingHand,
        throwingHand,
        city,
        state,
        country,
        positions,
        dailyBudget,
        startTime,
        endTime,
        objective,
        nickname,
        title,
        headline,
        link,
        media,
        mediaChanged,
        reset,
    } = viewModel;

    useEffect(() => {
        if (ad) {
            viewModelRef.current.load(ad);
        }
    }, [ad]);

    // Dialog Stepper Values
    const [activeStep, setActiveStep] = useState(0);
    const [nextStepDisabled, setNextStepDisabled] = useState(false);
    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };
    const handleBack = () => {
        if (activeStep === 0) {
            setOpen(false);
        } else {
            setActiveStep((prevActiveStep) => prevActiveStep - 1);
        }
    };

    async function saveAd() {
        const data = {
            ageMin: ageMin !== "" ? parseInt(ageMin) : null,
            ageMax: ageMax !== "" ? parseInt(ageMax) : null,
            hittingHand: hittingHand !== "" ? hittingHand : null,
            throwingHand: throwingHand !== "" ? throwingHand : null,
            city: city?.value ?? null,
            state: state?.value ?? null,
            country: country?.value ?? null,
            positions: positions.length > 0 ? positions : null,
            dailyBudget: parseInt(dailyBudget),
            startTime: startTime?.unix() ?? null,
            endTime: endTime?.unix() ?? null,
            objective,
            nickname: nickname !== "" ? nickname : null,
            title,
            headline: headline !== "" ? headline : null,
            link: link !== "" ? link : null,
            media: media.length > 0 ? media : null,
            templateId: templateId,
        };
        const adId = ad !== undefined ? (
            await handleAdUpdate(data, setSnackbar, setSeverity, mediaChanged, ad)
        ) : (
            await handleAdCreate(data, setSnackbar, setSeverity)
        );
        return adId;
    }

    // Ad Create Handler
    async function handleFinish(submitAd: boolean) {
        setIsLoading(true);
        const adId = await saveAd();
        if (adId !== -1) {
            if (!submitAd) {
                setSeverity("success");
                setSnackbar("Ad saved as a draft.");
            } else {
                const submitResponse = await adService.submit(adId);
                if (submitResponse) {
                    setSeverity("success");
                    setSnackbar("Successfully submitted ad for review.");
                } else {
                    setSeverity("error");
                    setSnackbar("There was an error submitting the ad for review, but it has been saved as a draft.");
                }
            }
            setOpen(false);
            setActiveStep(0);
            reset();
            fetchData();
        }
        setIsLoading(false);
    }

    // Change Payment Method Handler
    async function handleChangePaymentMethod() {
        setIsLoading(true);
        const adId = await saveAd();
        if (adId !== -1) {
            setSeverity("success");
            setSnackbar("Ad saved as a draft.");
            await fetchData();
            await timeout(2000); // wait 2 seconds before redirecting
            navigate("/settings/billing");
        } else {
            setIsLoading(false);
        }
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={() => { setOpen(false) }}
                fullWidth={true}
                maxWidth="lg"
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <LoadingOverlay open={isLoading} />
                <DialogTitle id="alert-dialog-title">Create New Campaign</DialogTitle>
                <DialogContent>
                    <Stepper activeStep={activeStep}>
                        {steps.map((label) => {
                            return (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                    {activeStep === 0 ? (
                        <ObjectivePage viewModel={viewModel} setNextStepDisabled={setNextStepDisabled} />
                    ) : activeStep === 1 ? (
                        <MediaPage
                            viewModel={viewModel}
                            setNextStepDisabled={setNextStepDisabled}
                            setSnackbar={setSnackbar}
                            setSeverity={setSeverity}
                        />
                    ) : (
                        <AudiencePage
                            viewModel={viewModel}
                            setNextStepDisabled={setNextStepDisabled}
                            isLoading={isLoading}
                            handleChangePaymentMethod={handleChangePaymentMethod}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleBack} disabled={isLoading}>
                        {activeStep === 0 ? "Close" : "Back"}
                    </Button>
                    {activeStep === 2 ? (
                        <>
                            <Button onClick={() => handleFinish(false)} disabled={nextStepDisabled || isLoading}>
                                Save as Draft
                            </Button>
                            <Button onClick={() => handleFinish(true)} disabled={nextStepDisabled || isLoading}>
                                Submit for Review
                            </Button>
                        </>
                    ) : (
                        <Button onClick={handleNext} disabled={nextStepDisabled}>
                           Next
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <FormSnackbar
                message={snackbar}
                setMessage={setSnackbar}
                severity={severity}
            />
        </>
    )
}

export default CreateCampaign;
